import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { FolderEmptyIcon } from '@/UI/Icons';
import { useTheme } from '@/UI/theme.exports';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenHorVer } from '@/UI/styles';
import { FolderLargeEmptyIcon } from '@/UI/Icons/FolderLargeEmptyIcon';

interface FolderThumbnailProps {
  posters: string[];
  count: number;
}

export function FolderThumbnail({ posters, count }: FolderThumbnailProps) {
  const theme = useTheme();
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (count === 0) {
    return (
      <FlexCenHorVer
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: isFplMigrationEnabled ? cssVars.color.bg : color.Black92,
          borderRadius: 1,
        }}
      >
        <FolderLargeEmptyIcon width={40} height={40} color={token.text} />
      </FlexCenHorVer>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: isFplMigrationEnabled ? cssVars.color.bgSecondary : color.Black88,
        borderRadius: 1,
        padding: 1,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gridTemplateRows: 'repeat(2, 1fr)',
          gap: 0.375,
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
      >
        {[0, 1, 2, 3].map((index) => {
          const poster = posters[index];
          const isLastSlot = index === 3;
          const extraCount = count - 4;
          const shouldShowOverlay = isLastSlot && extraCount > 0;

          if (!poster) {
            return <Box key={index} />;
          }

          // Check if this is a folder icon
          if (poster === 'folder') {
            return (
              <FlexCenHorVer
                key={index}
                sx={{
                  borderRadius: 0.375,
                  bgcolor: isFplMigrationEnabled ? cssVars.color.bg : color.Black92,
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                }}
              >
                <FolderEmptyIcon
                  width={16}
                  height={16}
                  color={token.text}
                  style={{ opacity: shouldShowOverlay ? 0.5 : 1 }}
                />
                {shouldShowOverlay && <Box sx={{ position: 'absolute', inset: 0, bgcolor: color.Black64_T }} />}
              </FlexCenHorVer>
            );
          }

          return (
            <Box
              key={index}
              sx={{
                borderRadius: 0.375,
                overflow: 'hidden',
                backgroundImage: shouldShowOverlay
                  ? `linear-gradient(90deg, ${color.Black64_T} 0%, ${color.Black64_T} 100%), url(${poster})`
                  : `url(${poster})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                width: '100%',
                height: '100%',
              }}
            />
          );
        })}
        {count > 4 && (
          <FlexCenHorVer
            sx={{
              position: 'absolute',
              bottom: 0.25,
              right: 0.25,
              color: token.text,
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: theme.typography.fontFamily,
              width: 'calc(50% - 4.5px)',
              height: 'calc(50% - 4.5px)',
            }}
          >
            +{count - 4}
          </FlexCenHorVer>
        )}
      </Box>
    </Box>
  );
}
